/**
 * Config — validation and normalization of init() parameters
 */

const DEFAULTS = {
  userId: null,
  locale: 'ru',
  showCustomSearchField: false,
  customParams: {},
  limit: 20,
  debounceMs: 300,
  suggestMinChars: 3,
  zIndex: 9999,
};

const REQUIRED_FIELDS = ['apiKey', 'selector', 'apiBaseUrl'];

export default class Config {
  /**
   * @param {Object} options - Raw options from GarderoboSearch.init()
   */
  constructor(options) {
    if (!options || typeof options !== 'object') {
      throw new Error('[GarderoboSearch] Options must be a non-null object');
    }

    for (const field of REQUIRED_FIELDS) {
      if (!options[field]) {
        throw new Error(`[GarderoboSearch] Missing required option: "${field}"`);
      }
    }

    if (typeof options.apiKey !== 'string') {
      throw new Error('[GarderoboSearch] "apiKey" must be a string');
    }

    if (typeof options.selector !== 'string') {
      throw new Error('[GarderoboSearch] "selector" must be a string');
    }

    if (typeof options.apiBaseUrl !== 'string') {
      throw new Error('[GarderoboSearch] "apiBaseUrl" must be a string');
    }

    // Merge defaults with provided options
    const merged = { ...DEFAULTS, ...options };

    // Normalize apiBaseUrl — strip trailing slash
    merged.apiBaseUrl = merged.apiBaseUrl.replace(/\/+$/, '');

    // Validate optional field types
    if (merged.userId !== null && typeof merged.userId !== 'string') {
      throw new Error('[GarderoboSearch] "userId" must be a string or null');
    }

    if (typeof merged.locale !== 'string') {
      throw new Error('[GarderoboSearch] "locale" must be a string');
    }

    if (typeof merged.showCustomSearchField !== 'boolean') {
      throw new Error('[GarderoboSearch] "showCustomSearchField" must be a boolean');
    }

    if (typeof merged.customParams !== 'object' || merged.customParams === null || Array.isArray(merged.customParams)) {
      throw new Error('[GarderoboSearch] "customParams" must be a plain object');
    }

    if (typeof merged.limit !== 'number' || merged.limit < 1) {
      throw new Error('[GarderoboSearch] "limit" must be a positive number');
    }

    if (typeof merged.debounceMs !== 'number' || merged.debounceMs < 0) {
      throw new Error('[GarderoboSearch] "debounceMs" must be a non-negative number');
    }

    if (typeof merged.suggestMinChars !== 'number' || merged.suggestMinChars < 1) {
      throw new Error('[GarderoboSearch] "suggestMinChars" must be a positive number');
    }

    if (typeof merged.zIndex !== 'number') {
      throw new Error('[GarderoboSearch] "zIndex" must be a number');
    }

    // Freeze and assign all properties
    Object.assign(this, merged);
    Object.freeze(this);
  }
}
